import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { forkJoin, Observable } from 'rxjs';
import { Catalogo, Puntos } from 'src/app/models/catalogos.interface';
import { Enfermera } from 'src/app/models/enfermera.interface';
import { Especialidad } from 'src/app/models/especialidad.interface';
import { Estado } from 'src/app/models/estado.interface';
import { Hospital } from 'src/app/models/hospital.interface';
import { HttpRespuesta } from 'src/app/models/http-respuesta.interface';
import { Institucion } from 'src/app/models/institucion.interface';
import { Medico } from 'src/app/models/medico.interface';
import { NuevoUsuarioAlta } from 'src/app/models/nuevo-usuario.interface';
import { Supervisor } from 'src/app/models/supervisor.interface';
import { TipoDropdown } from 'src/app/models/tipo-dropdown.interface';
import { UsuarioSesion } from 'src/app/models/usuario-sesion.interface';
import { AdministradorService } from 'src/app/services/administrador.service';
import { ROLE } from 'src/app/shared/constantes/pages-usuarios';

type TipoUsuarioActualizacion = 'medico' | 'enfermera' | 'supervisor';
type RegistroActualizable = Medico | Enfermera | Supervisor;

@Component({
  selector: 'app-administrador-usuario-actualizar',
  templateUrl: './administrador-usuario-actualizar.component.html',
  styleUrls: ['./administrador-usuario-actualizar.component.scss'],
  providers: [MessageService]
})
export class AdministradorUsuarioActualizarComponent implements OnInit {
  readonly ROLE = ROLE;

  formUsuario: FormGroup;
  hospitales: Hospital[] = [];
  especialidades: Especialidad[] = [];
  puestos: Catalogo[] = [];
  instituciones: TipoDropdown[] = [];
  estados: TipoDropdown[] = [];
  roles: Catalogo[] = [];
  niveles: Catalogo[] = [];
  puntos: TipoDropdown[] = [];

  tipoUsuario: TipoUsuarioActualizacion = 'medico';
  idTipoUsuario: string = '';
  idUsuario: number | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private administradorService: AdministradorService
  ) {
    this.formUsuario = this.createUsuarioFormulario();
    this.confiChange();
  }

  ngOnInit(): void {
    const tipo = this.route.snapshot.paramMap.get('tipo');
    const id = this.route.snapshot.paramMap.get('id');

    if (!this.esTipoUsuarioActualizacion(tipo) || !id) {
      this.showError('Tipo de usuario invalido');
      this.router.navigate(['/administrador/usuarios']);
      return;
    }

    this.tipoUsuario = tipo;
    this.idTipoUsuario = id;
    this.obtenerInformacion();
  }

  onSubmit(): void {
    if (this.formUsuario.valid) {
      if (!this.idUsuario) {
        this.showError('No se encontro el usuario relacionado');
        return;
      }

      const usuario = this.formUsuario.getRawValue() as NuevoUsuarioAlta;
      this.administradorService
        .updateUsuario(this.idUsuario, usuario)
        .subscribe({
          next: (resp) => {
            this.showSuccess(resp.mensaje);
            this.router.navigate([this.rutaRegreso()]);
          },
          error: (err: HttpErrorResponse) => {
            this.showError(err.error?.message || 'No se pudo actualizar el usuario');
            console.error(err);
          }
        });
    } else {
      this.showError('Por favor complete todos los campos requeridos');
      this.markFormGroupTouched(this.formUsuario);
    }
  }

  obtenerInformacion(): void {
    forkJoin({
      subInstituciones: this.administradorService.getInstituciones(),
      subEstados: this.administradorService.getEstados(),
      subEspecialidades: this.administradorService.getEspecialidades(),
      subPuestos: this.administradorService.getPuestos(),
      subRoles: this.administradorService.getRolesPrincipales(),
      subPuntos: this.administradorService.getPuntos(),
      subNiveles: this.administradorService.getNiveles(),
      subUsuario: this.getRegistroActualizable()
    }).subscribe({
      next: (resp) => {
        this.fullCatalogoInstituciones(resp.subInstituciones);
        this.fullCatalogoEstados(resp.subEstados);
        this.fullCatalogoEspecialidades(resp.subEspecialidades);
        this.fullCatalogoPuestos(resp.subPuestos);
        this.fullCatalogoRoles(resp.subRoles);
        this.fullCatalogoPuntos(resp.subPuntos);
        this.fullCatalogoNiveles(resp.subNiveles);
        this.patchFormulario(resp.subUsuario.datos);
      },
      error: (err: HttpErrorResponse) => {
        this.showError(err.error?.message || 'No se pudo cargar la informacion del usuario');
        console.error(err);
      }
    });
  }

  fullCatalogoEspecialidades(items: Especialidad[]): void {
    this.especialidades = [...items];
  }

  fullCatalogoPuestos(items: Catalogo[]): void {
    this.puestos = [...items];
  }

  fullCatalogoRoles(items: Catalogo[]): void {
    this.roles = [...items];
  }

  fullCatalogoNiveles(items: Catalogo[]): void {
    this.niveles = [...items];
  }

  fullCatalogoHospitales(items: Hospital[]): void {
    this.hospitales = [...items];
  }

  fullCatalogoInstituciones(instituciones: Institucion[]): void {
    this.instituciones = instituciones.map((item: Institucion) => this.institucionToTipoDropdown(item));
  }

  fullCatalogoEstados(estados: Estado[]): void {
    this.estados = estados.map((item: Estado) => this.estadoToTipoDropdown(item));
  }

  fullCatalogoPuntos(puntos: Puntos[]): void {
    this.puntos = puntos.map((item: Puntos) => this.puntosToTipoDropdown(item));
  }

  institucionToTipoDropdown(institucion: Institucion): TipoDropdown {
    return { value: `${institucion.id}`, label: institucion.cvInstitucion };
  }

  estadoToTipoDropdown(estado: Estado): TipoDropdown {
    return { value: `${estado.id}`, label: estado.nomEstado };
  }

  puntosToTipoDropdown(puntos: Puntos): TipoDropdown {
    return { value: `${puntos.id}`, label: `${puntos.puntos}` };
  }

  createUsuarioFormulario(): FormGroup {
    return this.fb.group({
      // usuario: ['', [Validators.email]],
      // password: ['', [Validators.maxLength(18)]],
      alias: ['', Validators.required],
      nombre: ['', Validators.required],
      primerApellido: ['', Validators.required],
      segundoApellido: [''],
      telefonoMovil: [''],
      fk_nivel: [4, Validators.required],
      fk_rol: [{ value: null, disabled: true }, Validators.required],
      activo: [1],

      medico: this.fb.group({
        fk_estado: [''],
        fk_institucion: [''],
        fk_especialidad: [''],
        fk_hospital: [''],
        fk_puesto: [''],
        fk_residencia: [''],
        noConsultorio: [''],
        telefonoMovil: [''],
        telefonoConsultorio: [''],
        hospitalConsultorioPrivado: [''],
        noConsultorioDireccionPrivado: [''],
        nomConsultorioDireccionPrivado: [''],
        telefonoConsultorioPrivado: [''],
        fk_puntos: ['1', Validators.required],
        puntos: [500],
        fk_puntos_seg: ['1', Validators.required],
        puntos_seg: [500]
      }),

      enfermera: this.fb.group({
        puntos: [500],
        fk_puntos: ['1'],
        fk_supervisor: [null]
      }),

      supervisor: this.fb.group({
        correo: [''],
        telefonoMovil: ['']
      })
    });
  }

  confiChange(): void {
    this.formUsuario.get('medico.fk_estado')?.valueChanges.subscribe(() => { this.getHospitales(); });
    this.formUsuario.get('medico.fk_institucion')?.valueChanges.subscribe(() => { this.getHospitales(); });
  }

  getHospitales(): void {
    const idInstitucion = Number(this.formUsuario.get('medico.fk_institucion')?.value);
    const idEstado = Number(this.formUsuario.get('medico.fk_estado')?.value);

    if (idInstitucion && idEstado) {
      this.administradorService.getHospitales(idInstitucion, idEstado).subscribe({
        next: (resp) => {
          this.fullCatalogoHospitales(resp);
        },
        error: (err: HttpErrorResponse) => {
          this.showError(err.error?.message || 'No se pudieron cargar los hospitales');
          console.error(err);
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate([this.rutaRegreso()]);
  }

  get f() {
    return this.formUsuario.controls;
  }

  private getRegistroActualizable(): Observable<HttpRespuesta<RegistroActualizable>> {
    switch (this.tipoUsuario) {
      case 'medico':
        return this.administradorService.getMedico(this.idTipoUsuario) as Observable<HttpRespuesta<RegistroActualizable>>;
      case 'enfermera':
        return this.administradorService.getEnfermera(this.idTipoUsuario) as Observable<HttpRespuesta<RegistroActualizable>>;
      case 'supervisor':
        return this.administradorService.getSupervisor(this.idTipoUsuario) as Observable<HttpRespuesta<RegistroActualizable>>;
    }
  }

  private patchFormulario(registro: RegistroActualizable): void {
    switch (this.tipoUsuario) {
      case 'medico':
        this.patchMedico(registro as Medico);
        break;
      case 'enfermera':
        this.patchEnfermera(registro as Enfermera);
        break;
      case 'supervisor':
        this.patchSupervisor(registro as Supervisor);
        break;
    }
  }

  private patchMedico(medico: Medico): void {
    this.patchDatosGenerales(medico.usuario, ROLE.MEDICO, medico);

    const medicoExtra = medico as Medico & {
      fk_estado?: number | string | null;
      fk_institucion?: number | string | null;
      hospital?: Partial<Hospital>;
      nomConsultorioDireccionPrivado?: string | null;
    };

    const fkEstado = medicoExtra.fk_estado ?? medicoExtra.hospital?.fk_estado ?? '';
    const fkInstitucion = medicoExtra.fk_institucion ?? medicoExtra.hospital?.fk_institucion ?? '';

    this.formUsuario.patchValue({
      medico: {
        fk_estado: this.toDropdownValue(fkEstado),
        fk_institucion: this.toDropdownValue(fkInstitucion),
        fk_especialidad: medico.fk_especialidad ?? '',
        fk_hospital: medico.fk_hospital ?? '',
        fk_puesto: medico.fk_puesto ?? '',
        fk_residencia: medico.fk_residencia ?? '',
        noConsultorio: medico.noConsultorio ?? '',
        telefonoMovil: medico.telefonoMovil ?? '',
        telefonoConsultorio: medico.telefonoConsultorio ?? '',
        hospitalConsultorioPrivado: medico.hospitalConsultorioPrivado ?? '',
        noConsultorioDireccionPrivado: medico.noConsultorioDireccionPrivado ?? '',
        nomConsultorioDireccionPrivado: medicoExtra.nomConsultorioDireccionPrivado ?? '',
        telefonoConsultorioPrivado: medico.telefonoConsultorioPrivado ?? '',
        fk_puntos: this.toDropdownValue(medico.fk_puntos ?? 1),
        puntos: medico.puntos ?? 500,
        fk_puntos_seg: this.toDropdownValue(medico.fk_puntos_seg ?? 1),
        puntos_seg: medico.puntos_seg ?? 500
      }
    }, { emitEvent: false });

    this.getHospitales();
  }

  private patchEnfermera(enfermera: Enfermera): void {
    this.patchDatosGenerales(enfermera.usuario, ROLE.ENFERMERA, enfermera);

    this.formUsuario.patchValue({
      enfermera: {
        puntos: enfermera.puntos ?? 500,
        fk_puntos: this.toDropdownValue(enfermera.fk_puntos ?? 1),
        fk_supervisor: enfermera.fk_supervisor ?? null
      }
    }, { emitEvent: false });
  }

  private patchSupervisor(supervisor: Supervisor): void {
    this.patchDatosGenerales(supervisor.usuario, ROLE.SUPERVISOR, supervisor);

    this.formUsuario.patchValue({
      supervisor: {
        correo: supervisor.correo ?? supervisor.usuario?.usuario ?? '',
        telefonoMovil: supervisor.telefonoMovil ?? supervisor.usuario?.telefonoMovil ?? ''
      }
    }, { emitEvent: false });
  }

  private patchDatosGenerales(usuario: UsuarioSesion | undefined, rol: number, registro: RegistroActualizable): void {
    const registroDatos = registro as unknown as Record<string, unknown>;
    const correo = this.toStringValue(registroDatos['correo']);

    this.idUsuario = usuario?.id ?? this.toNumberValue(registroDatos['fk_usuario']);

    this.formUsuario.patchValue({
      usuario: usuario?.usuario ?? correo,
      password: usuario?.password ?? '',
      alias: usuario?.alias ?? '',
      nombre: usuario?.nombre ?? this.toStringValue(registroDatos['nombre']),
      primerApellido: usuario?.primerApellido ?? this.toStringValue(registroDatos['primerApellido']),
      segundoApellido: usuario?.segundoApellido ?? this.toStringValue(registroDatos['segundoApellido']),
      telefonoMovil: usuario?.telefonoMovil ?? this.toStringValue(registroDatos['telefonoMovil']),
      fk_nivel: usuario?.fk_nivel ?? 4,
      fk_rol: rol,
      activo: usuario?.activo ?? 1
    }, { emitEvent: false });

    this.formUsuario.get('fk_rol')?.disable({ emitEvent: false });
  }

  private esTipoUsuarioActualizacion(tipo: string | null): tipo is TipoUsuarioActualizacion {
    return tipo === 'medico' || tipo === 'enfermera' || tipo === 'supervisor';
  }

  private toDropdownValue(value: unknown): string {
    return value === null || value === undefined ? '' : `${value}`;
  }

  private toStringValue(value: unknown): string {
    return value === null || value === undefined ? '' : `${value}`;
  }

  private toNumberValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private rutaRegreso(): string {
    switch (this.tipoUsuario) {
      case 'medico':
        return '/administrador/medicos';
      case 'enfermera':
        return '/administrador/enfermeras';
      case 'supervisor':
        return '/administrador/supervisores';
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  showSuccess(mensaje: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Exito',
      detail: mensaje
    });
  }

  showError(mensaje: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: mensaje
    });
  }
}